import { fetchLatestAppEvent, getAppRelaySet, ndkActions } from '@/lib/stores/ndk'
import { FEATURED_ITEMS_CONFIG } from '@/lib/schemas/featured'
import type { FeaturedProducts, FeaturedCollections, FeaturedAuctions, FeaturedUsers } from '@/lib/schemas/featured'
import { configKeys } from '@/queries/queryKeyFactory'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { filterBlacklistedProductCoords, filterBlacklistedCollectionCoords, filterBlacklistedPubkeys } from '@/lib/utils/blacklistFilters'

// --- DATA FETCHING FUNCTIONS ---

/**
 * Fetches featured products settings (kind 30405)
 * @param appPubkey The app's pubkey
 * @returns Featured products data or null
 */
export const fetchFeaturedProducts = async (appPubkey: string): Promise<FeaturedProducts | null> => {
	const event = await fetchLatestAppEvent({
		kinds: [FEATURED_ITEMS_CONFIG.PRODUCTS.kind],
		authors: [appPubkey],
		'#d': [FEATURED_ITEMS_CONFIG.PRODUCTS.dTag],
	})

	if (!event) return null

	// Extract product coordinates from 'a' tags and filter out blacklisted ones
	const rawProducts = event.tags.filter((tag) => tag[0] === 'a' && tag[1]?.startsWith('30402:')).map((tag) => tag[1])
	const featuredProducts = filterBlacklistedProductCoords(rawProducts)

	return {
		featuredProducts,
		lastUpdated: event.created_at || Date.now() / 1000,
	}
}

/**
 * Fetches featured collections settings (kind 30003)
 * @param appPubkey The app's pubkey
 * @returns Featured collections data or null
 */
export const fetchFeaturedCollections = async (appPubkey: string): Promise<FeaturedCollections | null> => {
	const event = await fetchLatestAppEvent({
		kinds: [FEATURED_ITEMS_CONFIG.COLLECTIONS.kind],
		authors: [appPubkey],
		'#d': [FEATURED_ITEMS_CONFIG.COLLECTIONS.dTag],
	})

	if (!event) return null

	// Extract collection coordinates from 'a' tags and filter out blacklisted ones
	const rawCollections = event.tags.filter((tag) => tag[0] === 'a' && tag[1]?.startsWith('30405:')).map((tag) => tag[1])
	const featuredCollections = filterBlacklistedCollectionCoords(rawCollections)

	return {
		featuredCollections,
		lastUpdated: event.created_at || Date.now() / 1000,
	}
}

/**
 * Fetches featured auctions settings (kind 30409)
 * @param appPubkey The app's pubkey
 * @returns Featured auctions data or null
 */
export const fetchFeaturedAuctions = async (appPubkey: string): Promise<FeaturedAuctions | null> => {
	const event = await fetchLatestAppEvent({
		kinds: [FEATURED_ITEMS_CONFIG.AUCTIONS.kind],
		authors: [appPubkey],
		'#d': [FEATURED_ITEMS_CONFIG.AUCTIONS.dTag],
	})

	if (!event) return null

	const rawAuctions = event.tags.filter((tag) => tag[0] === 'a' && tag[1]?.startsWith('30408:')).map((tag) => tag[1])
	const featuredAuctions = rawAuctions.filter((coords) => {
		const parts = coords.split(':')
		if (parts.length !== 3 || !parts[1] || !parts[2]) return false
		return filterBlacklistedPubkeys([parts[1]]).length > 0
	})

	return {
		featuredAuctions,
		lastUpdated: event.created_at || Date.now() / 1000,
	}
}

/**
 * Fetches featured users settings (kind 30000)
 * @param appPubkey The app's pubkey
 * @returns Featured users data or null
 */
export const fetchFeaturedUsers = async (appPubkey: string): Promise<FeaturedUsers | null> => {
	const event = await fetchLatestAppEvent({
		kinds: [FEATURED_ITEMS_CONFIG.USERS.kind],
		authors: [appPubkey],
		'#d': [FEATURED_ITEMS_CONFIG.USERS.dTag],
	})

	if (!event) return null

	// Extract user pubkeys from 'p' tags and filter out blacklisted ones
	const rawUsers = event.tags.filter((tag) => tag[0] === 'p' && tag[1]).map((tag) => tag[1])
	const featuredUsers = filterBlacklistedPubkeys(rawUsers)

	return {
		featuredUsers,
		lastUpdated: event.created_at || Date.now() / 1000,
	}
}

// --- REACT QUERY HOOKS ---

const useFeaturedSettingsSubscription = (appPubkey: string, queryKey: readonly unknown[], expectedKind: number, expectedDTag: string) => {
	const queryClient = useQueryClient()
	const ndk = ndkActions.getNDK()

	useEffect(() => {
		if (!appPubkey || !ndk) return

		let latestEventTime = 0
		let receivedEose = false

		const subscription = ndk.subscribe(
			{
				kinds: [expectedKind],
				authors: [appPubkey],
				'#d': [expectedDTag],
			},
			{
				closeOnEose: false,
				relaySet: getAppRelaySet(),
				exclusiveRelay: true, // Reject stale copies from other relays in the pool
			},
		)

		subscription.on('event', (event) => {
			const eventTime = event.created_at ?? 0
			if (receivedEose && eventTime > latestEventTime) {
				void queryClient.invalidateQueries({ queryKey })
			}
			if (eventTime > latestEventTime) {
				latestEventTime = eventTime
			}
		})

		subscription.on('eose', () => {
			receivedEose = true
		})

		return () => {
			subscription.stop()
		}
	}, [appPubkey, expectedDTag, expectedKind, ndk, queryClient, queryKey])
}

/**
 * Hook to fetch featured products
 */
export const useFeaturedProducts = (appPubkey: string) => {
	const queryKey = configKeys.featuredProducts(appPubkey)
	useFeaturedSettingsSubscription(appPubkey, queryKey, FEATURED_ITEMS_CONFIG.PRODUCTS.kind, FEATURED_ITEMS_CONFIG.PRODUCTS.dTag)

	return useQuery({
		queryKey,
		queryFn: () => fetchFeaturedProducts(appPubkey),
		enabled: !!appPubkey,
		staleTime: 5 * 60 * 1000, // 5 minutes
	})
}

/**
 * Hook to fetch featured collections
 */
export const useFeaturedCollections = (appPubkey: string) => {
	const queryKey = configKeys.featuredCollections(appPubkey)
	useFeaturedSettingsSubscription(appPubkey, queryKey, FEATURED_ITEMS_CONFIG.COLLECTIONS.kind, FEATURED_ITEMS_CONFIG.COLLECTIONS.dTag)

	return useQuery({
		queryKey,
		queryFn: () => fetchFeaturedCollections(appPubkey),
		enabled: !!appPubkey,
		staleTime: 5 * 60 * 1000, // 5 minutes
	})
}

/**
 * Hook to fetch featured auctions
 */
export const useFeaturedAuctions = (appPubkey: string) => {
	const queryKey = configKeys.featuredAuctions(appPubkey)
	useFeaturedSettingsSubscription(appPubkey, queryKey, FEATURED_ITEMS_CONFIG.AUCTIONS.kind, FEATURED_ITEMS_CONFIG.AUCTIONS.dTag)

	return useQuery({
		queryKey,
		queryFn: () => fetchFeaturedAuctions(appPubkey),
		enabled: !!appPubkey,
		staleTime: 5 * 60 * 1000, // 5 minutes
	})
}

/**
 * Hook to fetch featured users
 */
export const useFeaturedUsers = (appPubkey: string) => {
	const queryKey = configKeys.featuredUsers(appPubkey)
	useFeaturedSettingsSubscription(appPubkey, queryKey, FEATURED_ITEMS_CONFIG.USERS.kind, FEATURED_ITEMS_CONFIG.USERS.dTag)

	return useQuery({
		queryKey,
		queryFn: () => fetchFeaturedUsers(appPubkey),
		enabled: !!appPubkey,
		staleTime: 5 * 60 * 1000, // 5 minutes
	})
}
